import { Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, UpdateStatusType, MessageType } from '../../../../models/enums';
import { GenericColDef } from '../../../../models/grid-context';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { cloneDeep } from 'lodash';

const NOT_CALCULATED = 'Not Calculated';

@Component({
    standalone: false,
    selector: 'res-storage',
    templateUrl: './storage.component.html',
    styleUrls: ['./storage.component.scss']
})
export class StorageComponent implements SettingsNavigationContentComponent, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    validChanges = true;

    gridName = 'Storage';
    automationId = 'storage';
    helpIndex = HelpIndex.org_storage_dlg;
    updateStatusType = UpdateStatusType.STORAGE;

    isBusy = false;
    isBusyMessage = 'Loading ...';
    currentOrg;
    totalStorageUsed: string;
    lastCalculatedDate;
    storageDescription: string;
    isCalculatingStorage: boolean;

    private entityChangedEvent: Subscription;

    constructor(private commonService: CommonService, private restService: RestService) {
        this.currentOrg = this.commonService?.currentUser?.customerName;
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data === UpdateStatusType.STORAGE && this.genericGrid) {
                this.genericGrid.loadData();
            }
        });
    }

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'depotName',
            automationId: this.automationId,
            minWidth: 160, maxWidth: 160, width: 160,
            suppressAutoSize: true,
            suppressMovable: true,
            lockVisible: true,
            cellStyle: params => {
                if (!params?.data?.isHeader) {
                    return {
                        'margin-left': '10px'
                    }
                }
                if (params?.data?.isHeader) {
                    return {
                        'background-color': '#f2f2f2',
                        'color': '#666'
                    }
                }
                return null;
            }
        },
        {
            headerName: 'Storage Used',
            field: 'usageInKb',
            automationId: this.automationId,
            minWidth: 110, maxWidth: 110, width: 110,
            suppressAutoSize: true,
            suppressMovable: true,
            lockVisible: true,
            cellStyle: params => {
                if (params?.data?.isHeader) {
                    return {
                        'background-color': '#f2f2f2',
                        'color': '#666'
                    }
                }
                return null;
            }
        },
        {
            headerName: 'Path',
            field: 'depotPath',
            automationId: this.automationId,
            minWidth: 70,
            suppressMovable: true,
            lockVisible: true,
            cellStyle: params => {
                if (params?.data?.isHeader) {
                    return {
                        'background-color': '#f2f2f2',
                        'color': '#666'
                    }
                }
                return null;
            }
        }
    );

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.storageAreaManager_listOrganizationStorageInfo,
        responseName: 'storages',
        data: {
            organizationHandle: this.commonService?.currentUser?.customerName
        }
    });

    responseCallback = (response: any): void => {
        if (response) {
            response.storages = [];
            this.storageDescription = response.organizationStorageAreas[0].description;
            const currentStorageUsages = cloneDeep(response.storageUsages);
            let storageUsed = 0;
            if (currentStorageUsages?.length > 0) {
                this.lastCalculatedDate = currentStorageUsages[0].lastCalculated ?? NOT_CALCULATED;
                currentStorageUsages.forEach(storage => {
                    storageUsed += parseInt(storage.usageInKb);
                    storage.usageInKb = storage.usageInKb && storage.usageInKb !== NOT_CALCULATED ? this.commonService.formatSizeUnits(storage.usageInKb * 1024) : '';
                    response.storages.push(storage);
                });
                this.totalStorageUsed = currentStorageUsages[0].lastCalculated ? this.commonService.formatSizeUnits(storageUsed * 1024) : NOT_CALCULATED;
            } else if (response.organizationStorageAreas[0]?.storagePaths) {
                const currentStoragePathsObj = response.organizationStorageAreas[0].storagePaths;
                Object.keys(currentStoragePathsObj).forEach(key => {
                    response.storages.push({
                        depotName: currentStoragePathsObj[key].name,
                        depotPath: currentStoragePathsObj[key].path,
                        usageInKb: this.commonService.formatSizeUnits(0)
                    });
                })
            }
            this.isCalculatingStorage = _.some(currentStorageUsages, (item) => item.processing);

            if (response.storages.length > 0) {
                response.storages = this.initializeGrids(response.storages);
            }
        }
    }

    calculateStorage(handle?: string): void {
        this.isCalculatingStorage = true;
        this.restService.post(Endpoint.realmManager_calculateStorageUsage, { orgNames: [this.currentOrg] }, true, [MessageType.entityChanged, this.updateStatusType]).then(response => {
            return;
        });
    }

    selectedTreeItemChanged = (): void => {
        return;
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    openHelp = (): void => this.commonService.openHelp(this.helpIndex);

    ngOnDestroy(): void {
        if (this.entityChangedEvent) {
            this.entityChangedEvent.unsubscribe();
        }
    }

    rowClassRules = {
        'roles-header-row': (params: any): any => {
            return params?.data?.isHeader;
        },
    };

    initializeGrids(storages: any): [] {
        let newStorage: any = [];

        const indexSection = [];
        indexSection.push({ depotName: 'Indexes', isHeader: true });

        const documentSection = [];

        storages.forEach(storage => {
            if (storage.depotName.includes('Document')) {
                documentSection.push(storage);
            }
            else {
                indexSection.push(storage);
            }
        });

        newStorage.push(...indexSection);

        if (documentSection.length > 0) {
            newStorage.push({ depotName: 'Documents', isHeader: true });
            newStorage.push(...documentSection);
        }

        return newStorage;
    }
}
